import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDateRange } from "@/state/DateRangeContext";
import { rollingUtcRangeDays, utcTodayYmd } from "@/utils/dateRangeUtc";

function labelForRange(startDate, endDate) {
  const a = new Date(`${startDate}T12:00:00Z`);
  const b = new Date(`${endDate}T12:00:00Z`);
  const fmt = (d, other) => {
    const o = { month: "short", day: "numeric" };
    if (d.getUTCFullYear() !== other.getUTCFullYear()) o.year = "numeric";
    return d.toLocaleDateString(undefined, o);
  };
  if (startDate === endDate) return fmt(a, a);
  return `${fmt(a, b)} — ${fmt(b, a)}`;
}

export default function DateRangePicker() {
  const { startDate, endDate, setRange } = useDateRange();
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDraftStart(startDate);
    setDraftEnd(endDate);
  }, [open, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const applyPreset = useCallback((preset) => {
    const today = utcTodayYmd();
    if (preset === "today") {
      setDraftStart(today);
      setDraftEnd(today);
      return;
    }
    if (preset === "7d") {
      const r = rollingUtcRangeDays(7);
      setDraftStart(r.startDate);
      setDraftEnd(r.endDate);
      return;
    }
    if (preset === "30d") {
      const r = rollingUtcRangeDays(30);
      setDraftStart(r.startDate);
      setDraftEnd(r.endDate);
      return;
    }
    if (preset === "90d") {
      const r = rollingUtcRangeDays(90);
      setDraftStart(r.startDate);
      setDraftEnd(r.endDate);
    }
  }, []);

  const apply = useCallback(() => {
    const today = utcTodayYmd();
    if (draftStart > draftEnd) return;
    if (draftEnd > today) return;
    const d0 = new Date(`${draftStart}T00:00:00Z`);
    const d1 = new Date(`${draftEnd}T00:00:00Z`);
    if ((d1 - d0) / 86400000 + 1 > 365) return;
    setRange({ startDate: draftStart, endDate: draftEnd });
    setOpen(false);
  }, [draftStart, draftEnd, setRange]);

  const activePreset = useMemo(() => {
    const today = utcTodayYmd();
    if (draftStart === today && draftEnd === today) return "today";
    for (const [id, days] of [
      ["7d", 7],
      ["30d", 30],
      ["90d", 90],
    ]) {
      const r = rollingUtcRangeDays(days);
      if (draftStart === r.startDate && draftEnd === r.endDate) return id;
    }
    return null;
  }, [draftStart, draftEnd]);

  const pill = useMemo(() => labelForRange(startDate, endDate), [startDate, endDate]);

  return (
    <div className="relative z-[60]" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="ui-btn-secondary inline-flex items-center gap-2 text-sm"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <i className="ri-calendar-line text-base" aria-hidden />
        <span className="max-w-[220px] truncate font-medium">{pill}</span>
        <i className="ri-arrow-down-s-line text-base opacity-60" aria-hidden />
      </button>
      {open ? (
        <div
          className="absolute right-0 z-[70] mt-2 w-[min(100vw-2rem,22rem)] rounded-xl border border-slate-200 bg-white p-4 shadow-lg ring-1 ring-black/5"
          role="dialog"
          aria-label="Date range"
        >
          <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">Presets</p>
          <div className="mb-4 flex flex-wrap gap-2">
            {[
              ["today", "Today"],
              ["7d", "Last 7 days"],
              ["30d", "Last 30 days"],
              ["90d", "Last 90 days"],
            ].map(([id, lab]) => {
              const selected = activePreset === id;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={selected}
                  className={
                    selected
                      ? "rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 ring-2 ring-slate-900"
                      : "rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200/80 hover:bg-slate-200/80"
                  }
                  onClick={() => applyPreset(id)}
                >
                  {lab}
                </button>
              );
            })}
          </div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">Custom (UTC)</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs font-medium text-slate-600">
              Start
              <input
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="ui-input mt-1 w-full text-sm"
              />
            </label>
            <label className="text-xs font-medium text-slate-600">
              End
              <input
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                max={utcTodayYmd()}
                className="ui-input mt-1 w-full text-sm"
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="ui-btn-secondary text-sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button type="button" className="ui-btn-primary text-sm" onClick={() => void apply()}>
              Apply
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
